import { useMemo } from 'react';
import { useSettingsStore } from '../stores/settingsStore';

type Gender = 'masculine' | 'feminine';

export interface LabelForms {
  // Forme plurali (per liste, menu, "Torna ai...")
  plural: string;          // "Leads", "Contatti", "Opportunità"
  pluralLower: string;     // "leads", "contatti", "opportunità"
  // Forme singolari (per "Nuovo...", "questo...", "Modifica...")
  singular: string;        // "Lead", "Contatto", "Opportunità"
  singularLower: string;   // "lead", "contatto", "opportunità"
  // Forme grammaticali (basate sul singolare)
  nuovo: string;           // "Nuovo" o "Nuova"
  questo: string;          // "questo" o "questa"
  il: string;              // "il" o "la" (o "l'" se inizia con vocale)
  del: string;             // "del " o "della " (o "dell'" se vocale): spazio finale gia' incluso
  // Alias per retrocompatibilità
  label: string;           // Alias per singular
  labelLower: string;      // Alias per singularLower
}

// Determina automaticamente il genere grammaticale italiano
function guessGender(word: string): Gender {
  const lowerWord = word.toLowerCase();
  // Parole che terminano in -a, -ione, -tà, -tù sono generalmente femminili
  if (
    lowerWord.endsWith('a') ||
    lowerWord.endsWith('ione') ||
    lowerWord.endsWith('ità') ||
    lowerWord.endsWith('tà') ||
    lowerWord.endsWith('tù')
  ) {
    return 'feminine';
  }
  // Eccezioni maschili comuni che terminano in -a
  const masculineExceptions = ['problema', 'sistema', 'tema', 'programma', 'cinema', 'clima'];
  if (masculineExceptions.includes(lowerWord)) {
    return 'masculine';
  }
  return 'masculine';
}

function createLabelForms(pluralLabel: string, singularLabel: string): LabelForms {
  const singular = singularLabel;
  const startsWithVowel = /^[aeiouAEIOU]/.test(singular);
  const gender = guessGender(singular);
  const isMasculine = gender === 'masculine';

  return {
    // Forme plurali
    plural: pluralLabel,
    pluralLower: pluralLabel.toLowerCase(),
    // Forme singolari
    singular,
    singularLower: singular.toLowerCase(),
    // Forme grammaticali
    nuovo: isMasculine ? 'Nuovo' : 'Nuova',
    questo: isMasculine ? 'questo' : 'questa',
    il: startsWithVowel ? "l'" : (isMasculine ? 'il' : 'la'),
    del: startsWithVowel ? "dell'" : (isMasculine ? 'del ' : 'della '),
    // Alias per retrocompatibilità (puntano al singolare, che è il caso più comune)
    label: singular,
    labelLower: singular.toLowerCase(),
  };
}

export function useMenuLabels() {
  const settings = useSettingsStore(state => state.settings);
  const menuLabels = settings?.menu_settings?.menu_labels;

  return useMemo(() => ({
    lead: createLabelForms(
      menuLabels?.leads_label || 'Leads',
      menuLabels?.leads_label_singular || 'Lead'
    ),
    client: createLabelForms(
      menuLabels?.clients_label || 'Clienti',
      menuLabels?.clients_label_singular || 'Cliente'
    ),
  }), [menuLabels]);
}
